/** "What changed": a refresh draft against the live page it would replace (word by word). */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { describeError } from '../../lib/describeError'
import { getLiveChanges, type LiveChanges as Changes } from '../api'
import { changeSummary, changeSummaryText } from './sourceChanges'
import { btn, errorText } from './ui'

const FIELDS: { key: 'title' | 'seo_title' | 'meta_description'; label: string }[] = [
  { key: 'title', label: 'Headline' }, { key: 'seo_title', label: 'Search title' }, { key: 'meta_description', label: 'Search description' },
]

function Diff({ c }: { c: Changes }) {
  const changed = FIELDS.filter((f) => (c[f.key][0] ?? '') !== (c[f.key][1] ?? ''))
  return (
    <div className="flex flex-col gap-2">
      {changed.map((f) => (
        <p key={f.key} className="m-0 font-ui text-[13px] text-paper-dim">
          <span className="font-semibold">{f.label}:</span> <del className="bg-radar-red/15">{c[f.key][0]}</del> <ins className="bg-radar-green/20 underline">{c[f.key][1]}</ins>
        </p>
      ))}
      <p className="m-0 font-ui text-[13px] font-semibold text-paper-dim">{changeSummaryText(changeSummary(c.ops))}</p>
      <div className="max-h-80 overflow-y-auto whitespace-pre-wrap rounded-lg border border-ink-500 p-4 font-serif text-[16px] leading-relaxed text-paper">
        {c.ops.map((o, i) => o.op === 'insert' ? <ins key={i} className="bg-radar-green/20 underline">{o.text}</ins>
          : o.op === 'delete' ? <del key={i} className="bg-radar-red/15">{o.text}</del> : <span key={i}>{o.text}</span>)}
      </div>
    </div>
  )
}

export function LiveChanges({ id }: { id: number }) {
  const [open, setOpen] = useState(false)
  // gcTime 0: every opening reads the draft as it is now (the body changes while it is edited)
  const q = useQuery({ queryKey: ['live-changes', id], queryFn: () => getLiveChanges(id), enabled: open, gcTime: 0, retry: false })
  return (
    <section aria-label="What changed" className="flex flex-col gap-3">
      <div>
        <button type="button" className={btn} aria-expanded={open} onClick={() => setOpen((o) => !o)}>{open ? 'Hide what changed' : 'What changed'}</button>
      </div>
      {open && q.isLoading && <p role="status" className="m-0 font-ui text-[13px] text-paper-dim">Loading the changes…</p>}
      {open && q.isError && <p role="alert" className={errorText}>Could not load the changes. {describeError(q.error)}</p>}
      {open && q.data && <Diff c={q.data} />}
    </section>
  )
}
